import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';

import { sendPasswordResetEmail } from 'firebase/auth';

import { useAuth } from '../../../context/Auth.context';
import { auth } from '../../../config/firebase';
import { authErrorMessage } from '../../../utils/authErrors';

/**
 * @param {string} [defaultEmail] pre-fills the address. Used by the trip
 *   invitation screen, where the invitation names one specific address and
 *   signing in with any other one will not open the trip — so making the
 *   traveller type it, and get it subtly wrong, is a pointless way to fail.
 */
export function LoginForm({ open, setOpen, defaultEmail = '' }) {
  const { onLogin } = useAuth();
  const [scroll] = React.useState('paper');
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [resetting, setResetting] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');

    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      await onLogin({ email, password });
      toast.success("Welcome back! Signed in successfully.");
      handleClose();
    } catch (error) {
      // Was falling through to `error.message` for anything unrecognised,
      // which put strings like "Firebase: Error (auth/too-many-requests)."
      // in front of travellers. The mapper is mirrored from the provider app
      // so both surfaces word the same failure identically.
      toast.error(authErrorMessage(error, 'An error occurred during sign in.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * "Forgot password?" was a Link with no onClick — a control that looked
   * functional and did nothing. It resets against whatever is in the email
   * field, so there is nothing extra to fill in.
   *
   * The confirmation is identical whether or not the address is registered:
   * saying "no such account" would make this an enumeration oracle.
   */
  const handleForgotPassword = async () => {
    const email = document.getElementById('email')?.value?.trim();

    if (!email) {
      setErrors({ email: 'Enter your email address first, then press this again.' });
      return;
    }

    setErrors({});
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      // Only a malformed address or a rate limit is worth reporting. Anything
      // else — notably "user not found" — must look like success.
      if (error?.code === 'auth/invalid-email' || error?.code === 'auth/too-many-requests') {
        toast.error(authErrorMessage(error));
        setResetting(false);
        return;
      }
    }

    toast.success(`If an account exists for ${email}, a reset link is on its way.`);
    setResetting(false);
  };

  const descriptionElementRef = React.useRef(null);
  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll={scroll}
        fullWidth
        maxWidth="xs"
        aria-labelledby="scroll-dialog-title"
      >
        <DialogContent dividers={scroll === 'paper'}>
          <Box
            sx={{
              marginTop: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography id="scroll-dialog-title" component="h1" variant="h5">
              Sign in
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                defaultValue={defaultEmail}
                autoFocus
                error={!!errors.email}
                helperText={errors.email}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password}
              />
              <FormControlLabel
                control={<Checkbox value="remember" color="primary" />}
                label="Remember me"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
              <Grid container>
                <Grid size="grow">
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleForgotPassword}
                    disabled={resetting}
                  >
                    {resetting ? 'Sending…' : 'Forgot password?'}
                  </Link>
                </Grid>
                <Grid>
                  <Link component="button" type="button" variant="body2">
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        {/* <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleClose}>Subscribe</Button>
        </DialogActions> */}
      </Dialog>
    </div>
  );
}
